// modules
import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { marked } from 'marked';
import parse from 'react-html-parser';
import { withTranslation } from "react-i18next";

import icon_chat from '../../../assets/images/ARMAN-AI-01.svg'
import icon_chat_holder from '../../../assets/images/ARMAN-AI-02.svg'
import person_chat from '../../../assets/images/ARMAN-AI-03.png'

import './markdown.css';

const ChatAI = ({ t }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleChat = () => setIsOpen(true);
  const closeChat = () => setIsOpen(false);


  // Chat AI
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState(" ");
  const [loading, setLoading] = useState(false);

  const [hasAnswer, setHasAnswer] = useState(false);

  const [messages, setMessages] = useState([]); // список { question, answer }

  const [shouldSend, setShouldSend] = useState(false);


  const handleInput = (e) => {
    e.target.style.height = "43px"; // сброс
    e.target.style.height = `${e.target.scrollHeight}px`; // новая высота
    setQuestion(e.target.value); // обновление состояния
  };

  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = '43px';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, [question]);


  const handleSend = async () => {
    const q = question;

    if (!q.trim()) {
      setAnswer(t("dashboard.chatAI.emptyError"));
      return;
    }

    setLoading(true);
    setAnswer(t("dashboard.chatAI.loading"));

    try {
      const escapeInput = (text) =>
        text.replace(/\\/g, "\\\\").replace(/"/g, '\\"').replace(/\n/g, "\\n");

      const safeQuestion = escapeInput(q);

      // === 1. Сжимаем контекст ===
      let contextSummary = "";
      const lastN = 4; // ⚠️ Используем только последние N сообщений
      if (messages.length > 0) {
        const recentMessages = messages.slice(-lastN).map(m => {
          const shortQ = m.question.slice(0, 200); // ограничим длину
          const shortA = m.answer.slice(0, 300);
          return `Q: ${shortQ}\nA: ${shortA}`;
        });

        const formatted = recentMessages.join("\n\n");

        const summaryRes = await fetch("https://openrouter.ai/api/v1/chat/completions", {
          method: "POST",
          headers: {
            "Authorization": "Bearer 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", // 🔐 Спрячь ключ на сервере
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            model: "mistralai/mistral-7b-instruct",
            messages: [
              {
                role: "system",
                content:
                  "Ты помощник. Сожми суть предыдущей беседы в 1 предложение. Не упоминай пользователя напрямую. " +
                  "Не включай никаких примеров кода, команд, кавычек, слэшей, переносов строк или других спецсимволов. " +
                  "Ответ должен быть чистым, безопасным, обычным текстом без форматирования.",
              },
              {
                role: "user",
                content: formatted
              }
            ],
            max_tokens: 120, // 💡 еще меньше
            temperature: 0.3
          })
        });

        const summaryData = await summaryRes.json();
        if (
          summaryData &&
          summaryData.choices &&
          Array.isArray(summaryData.choices) &&
          summaryData.choices.length > 0 &&
          summaryData.choices[0].message &&
          typeof summaryData.choices[0].message.content === "string"
        ) {
          contextSummary = summaryData.choices[0].message.content;
        }
      }

      contextSummary = contextSummary
        .replace(/[`"'\\]/g, "")      // удаляет кавычки, апострофы, бектики, слэши
        .replace(/[\r\n]+/g, " ")     // заменяет переносы строк на пробел
        .replace(/код:.+$/i, "")      // удаляет возможные фразы вида "код: ..."
        .trim();


      // === 2. Отправляем вопрос ===
      const payload = {
        question: contextSummary
          ? `Контекст: ${contextSummary} Вопрос: ${safeQuestion}`
          : safeQuestion
      };

      console.log(payload)

      const res = await fetch("https://armanpv.app.n8n.cloud/webhook/ai-helper", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const text = await res.text();

      if (!text.trim()) {
        await fetch("/api/telegram/callback", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: "AI Chat",
            mail: "",
            message: `Пустой ответ от ИИ на запрос:\n\`\`\`json\n${JSON.stringify(payload, null, 2)}\n\`\`\``,
          }),
        });


        setAnswer(t("dashboard.chatAI.rephrase"));
        setMessages((prev) => [...prev, { question: q, answer: t("dashboard.chatAI.rephrase") }]);
      } else {
        setMessages((prev) => [...prev, { question: q, answer: text }]);
        setAnswer(text);
      }

      setHasAnswer(true);
      setQuestion("");
      if (textareaRef.current) {
        textareaRef.current.style.height = "43px";
      }
    } catch (err) {
      console.error("Ошибка запроса:", err);
      setAnswer(t("dashboard.chatAI.error"));
    }

    setLoading(false);
  };




  const handleSuggestion = (text) => {
    setQuestion(prev => prev.length > 0 ? prev + '\n' + text : text);
    // setShouldSend(true); // триггерим отправку после обновления question
  };

  useEffect(() => {
    if (shouldSend) {
      handleSend();
      setShouldSend(false);
    }
  }, [question, shouldSend]);

  const handleClear = () => {
    setMessages([]);
    setAnswer(" ");
    setQuestion("");
    if (textareaRef.current) {
      textareaRef.current.style.height = "43px";
    }
    setHasAnswer(false);

    // УДАЛЯЕМ ИЗ localStorage
    localStorage.removeItem("chatMessages");
  };


  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault(); // чтобы не было переноса
      handleSend();
    }
  };

  const textareaRef = useRef(null);

  useEffect(() => {
    if (isOpen && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isOpen]);

  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, loading]);

  useEffect(function () {
    var activeBtn = null;        // текущая кнопка
    var docClickHandler = null;  // обработчик клика по документу
    var rafScheduled = false;    // троттлинг через rAF
  
    function removeBtn() {
      if (docClickHandler) {
        document.removeEventListener('click', docClickHandler, true);
        docClickHandler = null;
      }
      if (activeBtn && activeBtn.parentNode) {
        activeBtn.parentNode.removeChild(activeBtn); // без .remove() для старых браузеров
      }
      activeBtn = null;
    }
  
    function isInsideContent(node) {
      var el = node && (node.nodeType === 1 ? node : node.parentElement);
      while (el) {
        if (el.classList && el.classList.contains('content')) return true;
        el = el.parentElement;
      }
      return false;
    }
  
    function getRect(range) {
      // На iOS корректнее брать последний прямоугольник выделения
      var rects = range.getClientRects ? range.getClientRects() : null;
      if (rects && rects.length) return rects[rects.length - 1];
      return range.getBoundingClientRect();
    }
  
    var lastSelectionText = ""; // храним последнее выделение

function update() {
  rafScheduled = false;

  var selection = window.getSelection ? window.getSelection() : null;
  if (!selection || selection.isCollapsed) { removeBtn(); return; }

  var text = selection.toString().trim();
  if (!text) { removeBtn(); return; }

  lastSelectionText = text; // сохраняем текст здесь

  var range;
  try { range = selection.getRangeAt(0); } catch (e) { removeBtn(); return; }

  if (!isInsideContent(range.commonAncestorContainer)) { removeBtn(); return; }

  var rect = getRect(range);
  if (!rect || (rect.width === 0 && rect.height === 0)) { removeBtn(); return; }

  var top = rect.bottom + window.scrollY;
  var left = rect.left + window.scrollX;

  if (!activeBtn) {
    var btn = document.createElement('button');
    btn.id = 'ai-question-btn';
    btn.className = 'ai-question-btn';
    btn.textContent = t('dashboard.chatAI.ask');

    btn.style.position = 'absolute';
    btn.style.padding = '8px 12px';
    btn.style.borderRadius = '6px';
    btn.style.background = '#000';
    btn.style.color = '#fff';
    btn.style.boxShadow = '0 2px 8px rgba(0,0,0,0.2)';
    btn.style.zIndex = '9999';

    btn.onclick = function (e) {
      e.preventDefault();
      e.stopPropagation();

      var currentText = lastSelectionText; // 🔑 берём сохранённое

      setQuestion(function (prev) {
        return prev && prev.length ? (prev + '\n' + currentText) : currentText;
      });

      setIsOpen(true);
      removeBtn();
    };

    document.body.appendChild(btn);
    activeBtn = btn;

    docClickHandler = function (ev) {
      if (activeBtn && (ev.target === activeBtn || activeBtn.contains(ev.target))) return;
      removeBtn();
    };
    document.addEventListener('click', docClickHandler, true);
  }

  activeBtn.style.top = top + 'px';
  activeBtn.style.left = left + 'px';
}

  
    function scheduleUpdate() {
      if (rafScheduled) return;
      rafScheduled = true;
      if (window.requestAnimationFrame) window.requestAnimationFrame(update);
      else setTimeout(update, 16); // fallback
    }
  
    document.addEventListener('selectionchange', scheduleUpdate);
    document.addEventListener('mouseup', scheduleUpdate);
    document.addEventListener('touchend', scheduleUpdate);
    window.addEventListener('scroll', scheduleUpdate, true);
    window.addEventListener('resize', scheduleUpdate);
  
    return function () {
      removeBtn();
      document.removeEventListener('selectionchange', scheduleUpdate);
      document.removeEventListener('mouseup', scheduleUpdate);
      document.removeEventListener('touchend', scheduleUpdate);
      window.removeEventListener('scroll', scheduleUpdate, true);
      window.removeEventListener('resize', scheduleUpdate);
    };
  }, []);
  


  // загрузка
  useEffect(() => {
    const stored = localStorage.getItem("chatMessages");
    if (stored) {
      setMessages(JSON.parse(stored));
      setHasAnswer(true);
    }
  }, []);

  // сохранение
  useEffect(() => {
    if (messages.length > 0) {
      localStorage.setItem("chatMessages", JSON.stringify(messages));
    }
  }, [messages]);


  // Chat AI


  return (
    <ChatAIWrapper>
      <ChatOverlay isOpen={isOpen} onClick={closeChat} className='chat_overlay'></ChatOverlay>
      <ChatAIButton>
        <div className="relative group">
          <button className='chat_button' onClick={toggleChat} type='button'>
            <img src={icon_chat} alt='' />
            <img width={67} src={icon_chat_holder} alt='' />
          </button>
          {/* <button onClick={toggleChat} type="button" className="chat_btn" aria-label="Открыть чат">
            <svg xmlns="http://www.w3.org/2000/svg" width={24} height={24} viewBox="0 0 24 24" fill="currentColor">
              <path fill="none" d="M0 0h24v24H0z" />
              <path d="M20.7134 8.12811L20.4668 8.69379C20.2864 9.10792 19.7136 9.10792 19.5331 8.69379L19.2866 8.12811C18.8471 7.11947 18.0555 6.31641 17.0677 5.87708L16.308 5.53922C15.8973 5.35653 15.8973 4.75881 16.308 4.57612L17.0252 4.25714C18.0384 3.80651 18.8442 2.97373 19.2761 1.93083L19.5293 1.31953C19.7058 0.893489 20.2942 0.893489 20.4706 1.31953L20.7238 1.93083C21.1558 2.97373 21.9616 3.80651 22.9748 4.25714L23.6919 4.57612C24.1027 4.75881 24.1027 5.35653 23.6919 5.53922L22.9323 5.87708C21.9445 6.31641 21.1529 7.11947 20.7134 8.12811ZM20 11C20.6986 11 21.3694 10.8806 21.9929 10.6611C21.9976 10.7735 22 10.8865 22 11C22 15.4183 18.4183 19 14 19V22.5C9 20.5 2 17.5 2 11C2 6.58172 5.58172 3 10 3H14C14.1135 3 14.2265 3.00237 14.3389 3.00705C14.1194 3.63061 14 4.30136 14 5C14 8.31371 16.6863 11 20 11Z" />
            </svg>
          </button> */}
          {/* <div className="main_tooltip">
            <span>{ t("dashboard.chatAI.ask")}</span>
          </div> */}
        </div>
      </ChatAIButton>
      <ChatAIContent isOpen={isOpen} isExpanded={isExpanded}>
        <ChatBackground>
          <div className='OverlayHalf'></div>
          <div className='OverlayBlob'></div>
        </ChatBackground>
        <ChatWrapper isOpen={isOpen}>
          <ChatHeader className="container">
            <ClearButton type="button" onClick={handleClear}>
              <span>{t("dashboard.chatAI.clear")}</span>
            </ClearButton>
            <div className="flex items-center space-x-2">
              <ControlButton onClick={() => setIsExpanded(!isExpanded)} type="button">
                {isExpanded ? (
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path fill="none" d="M0 0h24v24H0z"></path><path d="M18 7H22V9H16V3H18V7ZM8 9H2V7H6V3H8V9ZM18 17V21H16V15H22V17H18ZM8 15V21H6V17H2V15H8Z"></path></svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path fill="none" d="M0 0h24v24H0z"></path><path d="M8 3V5H4V9H2V3H8ZM2 21V15H4V19H8V21H2ZM22 21H16V19H20V15H22V21ZM22 9H20V5H16V3H22V9Z"></path></svg>
                )}

              </ControlButton>
              <ControlButton onClick={closeChat} type="button">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path fill="none" d="M0 0h24v24H0z"></path><path d="M11.9997 10.5865L16.9495 5.63672L18.3637 7.05093L13.4139 12.0007L18.3637 16.9504L16.9495 18.3646L11.9997 13.4149L7.04996 18.3646L5.63574 16.9504L10.5855 12.0007L5.63574 7.05093L7.04996 5.63672L11.9997 10.5865Z"></path></svg>
              </ControlButton>
            </div>
          </ChatHeader>

          <ChatBody $hasAnswer={hasAnswer}>
            {!hasAnswer ? (
              <>
                <ChatWelcome className="container">
                  <div className="icon-wrapper">
                    {/* <svg xmlns="http://www.w3.org/2000/svg" width={24} height={24} viewBox="0 0 24 24" fill="currentColor"><path fill="none" d="M0 0h24v24H0z"></path><path d="M13.5 2C13.5 2.44425 13.3069 2.84339 13 3.11805V5H18C19.6569 5 21 6.34315 21 8V18C21 19.6569 19.6569 21 18 21H6C4.34315 21 3 19.6569 3 18V8C3 6.34315 4.34315 5 6 5H11V3.11805C10.6931 2.84339 10.5 2.44425 10.5 2C10.5 1.17157 11.1716 0.5 12 0.5C12.8284 0.5 13.5 1.17157 13.5 2ZM0 10H2V16H0V10ZM24 10H22V16H24V10ZM9 14.5C9.82843 14.5 10.5 13.8284 10.5 13C10.5 12.1716 9.82843 11.5 9 11.5C8.17157 11.5 7.5 12.1716 7.5 13C7.5 13.8284 8.17157 14.5 9 14.5ZM16.5 13C16.5 12.1716 15.8284 11.5 15 11.5C14.1716 11.5 13.5 12.1716 13.5 13C13.5 13.8284 14.1716 14.5 15 14.5C15.8284 14.5 16.5 13.8284 16.5 13Z"></path></svg> */}
                    <img width={164} src={person_chat} alt="" />
                  </div>
                  <div className="title">{t("dashboard.chatAI.greeting")}</div>
                  <div className="subtitle">{t("dashboard.chatAI.intro")}</div>
                </ChatWelcome>

                <ChatSuggestions className="container">
                  <button type='button' onClick={() => handleSuggestion(t("dashboard.chatAI.summaryTitle"))}>
                    <div className="font-medium">{t("dashboard.chatAI.summaryTitle")}</div>
                    <div className="text-muted">{t("dashboard.chatAI.summaryDesc")}</div>
                  </button>
                  <button type='button' onClick={() => handleSuggestion(t("dashboard.chatAI.simplifyTitle"))}>
                    <div className="font-medium">{t("dashboard.chatAI.simplifyTitle")}</div>
                    <div className="text-muted">{t("dashboard.chatAI.simplifyDesc")}</div>
                  </button>
                  <button type='button' onClick={() => handleSuggestion(t("dashboard.chatAI.practiceTitle"))}>
                    <div className="font-medium">{t("dashboard.chatAI.practiceTitle")}</div>
                    <div className="text-muted">{t("dashboard.chatAI.practiceDesc")}</div>
                  </button>
                </ChatSuggestions>

              </>
            ) : (
              <MessagesContainer className="container">
                {messages.map((msg, idx) => (
                  <MessagePair key={idx}>
                    <QuestionBubble>{msg.question}</QuestionBubble>
                    <AnswerBubble className='markdown-content'>
                      {parse(marked(msg.answer || ""))}
                    </AnswerBubble>
                  </MessagePair>
                ))}
                <div ref={messagesEndRef} />
              </MessagesContainer>

            )}
            {loading && (
              <svg fill="#9A9A9AFF" height={32} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><circle cx="4" cy="12" r="3"><animate id="spinner_jObz" begin="0;spinner_vwSQ.end-0.25s" attributeName="r" dur="0.75s" values="3;.2;3" /></circle><circle cx="12" cy="12" r="3"><animate begin="spinner_jObz.end-0.6s" attributeName="r" dur="0.75s" values="3;.2;3" /></circle><circle cx="20" cy="12" r="3"><animate id="spinner_vwSQ" begin="spinner_jObz.end-0.45s" attributeName="r" dur="0.75s" values="3;.2;3" /></circle></svg>
            )}
          </ChatBody>

          <ChatInputBox className="container">
            <ChatForm>
              <ChatTextarea ref={textareaRef} value={question} onChange={handleInput} onKeyDown={handleKeyDown} rows={1} placeholder={t("dashboard.chatAI.placeholder")} />
              <SendButton type="button" onClick={handleSend} disabled={loading} style={{ cursor: loading ? "not-allowed" : "pointer", }}>
                {!loading ? (
                  <svg xmlns="http://www.w3.org/2000/svg" width={16} height={16} viewBox="0 0 24 24" fill="currentColor"><path fill="none" d="M0 0h24v24H0z"></path><path d="M3 12.9999H9V10.9999H3V1.84558C3 1.56944 3.22386 1.34558 3.5 1.34558C3.58425 1.34558 3.66714 1.36687 3.74096 1.40747L22.2034 11.5618C22.4454 11.6949 22.5337 11.9989 22.4006 12.2409C22.3549 12.324 22.2865 12.3924 22.2034 12.4381L3.74096 22.5924C3.499 22.7255 3.19497 22.6372 3.06189 22.3953C3.02129 22.3214 3 22.2386 3 22.1543V12.9999Z"></path></svg>
                ) : (
                  <svg fill="#FFFFFF" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><circle cx="4" cy="12" r="3" opacity="1"><animate id="spinner_qYjJ" begin="0;spinner_t4KZ.end-0.25s" attributeName="opacity" dur="0.75s" values="1;.2" fill="freeze" /></circle><circle cx="12" cy="12" r="3" opacity=".4"><animate begin="spinner_qYjJ.begin+0.15s" attributeName="opacity" dur="0.75s" values="1;.2" fill="freeze" /></circle><circle cx="20" cy="12" r="3" opacity=".3"><animate id="spinner_t4KZ" begin="spinner_qYjJ.begin+0.3s" attributeName="opacity" dur="0.75s" values="1;.2" fill="freeze" /></circle></svg>
                )}
              </SendButton>
            </ChatForm>
          </ChatInputBox>
        </ChatWrapper>
      </ChatAIContent>
    </ChatAIWrapper>
  );
};

export const WrappedChatAI = withTranslation()(ChatAI);

const ChatOverlay = styled.div`
    position: absolute;
    inset: 0;
    background: rgba(0,0,0,.25);
    opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
    transition: opacity 0.2s ease-in-out;
    pointer-events: ${({ isOpen }) => (isOpen ? 'auto' : 'none')};
    z-index: ${({ isOpen }) => (isOpen ? '99' : '0')};
`;

const ChatAIWrapper = styled.div`
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 50;

  .container {
    width: 100%;
    margin-left: auto;
    margin-right: auto;
    padding-left: 1rem;
    padding-right: 1rem;

    @media (min-width: 640px) {
      max-width: 640px;
    }
    @media (min-width: 768px) {
      max-width: 768px;
    }
    @media (min-width: 1024px) {
      max-width: 1024px;
    }
    @media (min-width: 1280px) {
      max-width: 1280px;
    }
    @media (min-width: 1536px) {
      max-width: 1536px;
    }
  }
`;

const ChatAIButton = styled.div`
  position: fixed;
  pointer-events: auto;
  right: 24px;
  bottom: 24px;
  z-index: 1;

  @media screen and (max-width: 600px) {
    right: 8px;
    bottom: 68px;
  }

  .relative {
    position: relative;
  }

  .chat_button {
    position: relative;

    width: 84px;
    height: 84px;
    background: none;
    border: none;

    @media screen and (max-width: 600px) {
      width: 62px;
      height: 62px;
    }

    img:first-child {
      width: 100%;
      height: 100%;
    }

    img:last-child {
      position: absolute;
      top: -8px;
      right: -8px;
      transition: transform .5s;
      transform-origin: left bottom;

      @media screen and (max-width: 600px) {
        display: none;
      }
    }

    &:hover img:last-child {
      transform: scale(1.2);
    }
  }

  .chat_btn {
    width: 44px;
    height: 44px;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;
    position: relative;
    border-radius: 999px;
    background: #09090B;
    color: #FAFAFA;
    cursor: pointer;

    &::after {
      content: "";
      position: absolute;
      inset: 0;
      transition: transform 0.3s ease-in-out;
      transform: scale(1.05);
      background: conic-gradient(
        from 0deg,
        #6D28D9,  /* violet-700 */
        #FCD34D,  /* yellow-300 */
        #6D28D9
      );
      border-radius: 9999px;
      z-index: -1;
      animation: spin 3s ease-in-out infinite;
    }

    @keyframes spin {
      0% {
        transform: scale(1.1) rotate(0deg);
      }
      100% {
        transform: scale(1.1) rotate(360deg);
      }
    }
  }

  .main_tooltip {
    position: absolute;
    bottom: 100%;
    right: 0;
    margin-bottom: 10px;
    max-width: 28rem;
    width: max-content;
    background-color: #27272A; /* zinc-800 */
    color: #FAFAFA;            /* zinc-50 */
    padding: 10px 16px;
    border-radius: 0.375rem;
    font-size: 0.875rem;
    font-weight: 500;
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.2s ease, visibility 0.2s ease;
    z-index: 10;

    &::before {
      content: "";
      position: absolute;
      width: 9px;
      height: 9px;
      background-color: #27272A;
      right: 1rem;
      bottom: -4px;
      transform: rotate(45deg);
      border-top-left-radius: 2px;
    }
  }

  .group:hover .main_tooltip {
    opacity: 1;
    visibility: visible;
  }
`;

const ChatAIContent = styled.div`
    position: absolute;
    background: #fff;
    overflow: hidden;
    transition: opacity 0.2s ease-in-out;
    opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
    pointer-events: ${({ isOpen }) => (isOpen ? 'auto' : 'none')};
    z-index: ${({ isOpen }) => (isOpen ? '100' : '0')};
    ${({ isExpanded }) => isExpanded ? `
        right: 0;
        top: 0;
        bottom: 0;
        left: 0;
        border-radius: 0;
    ` : `
        right: 24px;
        top: 4rem;
        bottom: 2rem;
        border-radius: 0.75rem;
        min-width: 500px;
        max-width: 500px;

        @media (max-width: 768px) {
            right: 0;
            left: 0;
            top: 0;
            bottom: 0;
            border-radius: 0;
            min-width: 100%;
        }
    `}
`;

const ChatBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 10rem; /* h-40 */
  background: linear-gradient(to bottom, #C7D2FE, transparent); /* from-indigo-200 to-transparent */
  opacity: 1;
  transition: opacity 0.2s ease-in-out;

  .OverlayHalf {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 50%;
    background: linear-gradient(150deg, #BAE6FD 5%, transparent 25%); /* from-sky-200 to-transparent */
  }

  .OverlayBlob {
     position: absolute;
    width: 5rem;
    height: 5rem;
    background-color: #F9A8D4; /* pink-200 */
    border-radius: 9999px;
    filter: blur(32px);
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
  }
`;

const ChatWrapper = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  height: 100%;
  z-index: 1;
  pointer-events: ${({ isOpen }) => (isOpen ? 'auto' : 'none')};
`;

const ChatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 0;
  width: 100%;

  .flex {
    display: flex;
  }

  .items-center {
    align-items: center;
  }

  .space-x-2 > *:not(:first-child) {
    margin-left: 0.5rem; /* соответствует space-x-2 */
  }
`;

const ControlButton = styled.button`
  cursor: pointer;
  min-width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.375rem;
  background: white;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
  border: 1px solid #e4e4e7;
  transition: background 0.2s, color 0.2s;
  color: #000;

  &:hover {
    background: #f4f4f5;
    color: #2563eb;
  }

  &:active {
    background: #e4e4e7;
  }

  &:focus {
    border-color: #d4d4d8;
  }

  @media (max-width: 768px) {
    &.hide-on-mobile {
      display: none;
    }
  }
`;

const ClearButton = styled(ControlButton)`
  height: 32px;
  padding: 0 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
`;

const ChatBody = styled.div`
  display: flex;
  flex-direction: column;
  overflow: auto;

  /* Small custom scrollbar */
  scrollbar-width: thin;
  scrollbar-color: #d4d4d8 transparent;

  ${({ $hasAnswer }) =>
    $hasAnswer &&
    `background-color: white;
    border-top: 1px solid #e4e4e7;
    height: 100%;
  `}
`;

const ChatWelcome = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1rem 0;

  .icon-wrapper {
    // margin-top: -1.625rem;
    // margin-bottom: 0.625rem;
    // width: 3rem;
    // height: 3rem;
    // background: white;
    // border-radius: 0.75rem;
    // box-shadow: 0 1px 2px rgba(0,0,0,0.05);
    // border: 1px solid #e4e4e7;
    // display: flex;
    // justify-content: center;
    // align-items: center;
    // color: #615FFF;
    overflow: hidden;
  }

  .title {
    font-weight: 500;
    text-align: center;
    margin-bottom: 0.25rem;
  }

  .subtitle {
    font-size: 0.875rem;
    color: #71717a;
    text-align: center;
  }
`;

const ChatSuggestions = styled.div`
  padding: 1rem 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  button {
    display: block;
    border: 1px solid #e4e4e7;
    background: white;
    border-radius: 0.375rem;
    padding: 0.625rem;
    transition: box-shadow 0.2s;
    color: black;
    text-align: left;
    line-height: unset;

    &:hover {
      box-shadow: 0 2px 4px rgba(0,0,0,0.06);
    }

    .font-medium {
      font-weight: 500;
    }

    .text-muted {
      color: #71717a;
    }
  }
`;

const ChatInputBox = styled.div`
  padding: 1rem 0;
  border-top: 1px solid #e4e4e7;
  margin-top: auto;
`;

const ChatForm = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 1rem;
`;

const ChatTextarea = styled.textarea`
  width: 100%;
  background: #f4f4f5;
  border: 2px solid #e4e4e7;
  outline: none;
  resize: none;
  border-radius: 0.375rem;
  padding: 0.5rem;
  height: 40px;
  font-size: 1rem;
  line-height: 1.75;
  min-height: 40px;
  max-height: 8rem;
  overflow-y: auto;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const SendButton = styled.button`
  cursor: pointer;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: 0.5rem;
  background: #4f46e5;
  border: 2px solid #4338ca;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 2px rgba(0,0,0,0.1);
  transition: filter 0.2s;

  &:hover {
    filter: brightness(1.1);
  }
`;

// Обёртка для всего списка сообщений
const MessagesContainer = styled.div`
  padding-top: 1rem;
  padding-bottom: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
`;

// Один блок вопрос+ответ
const MessagePair = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

// Вопрос (справа)
const QuestionBubble = styled.div`
  align-self: flex-end;
  background-color: #f4f4f5;
  padding: 0.5rem 0.75rem;
  border-radius: 0.375rem;
  max-width: 80%;
`;

// Ответ (слева)
const AnswerBubble = styled.div`
  background-color: #ffffff;
  padding: 0.5rem 0.75rem;
  border-radius: 0.375rem;
  border: 1px solid #e4e4e7;
  max-width: 80%;
  word-break: break-word;
`;
